import { AGENT_ROUTE_ALIASES } from "./agentRouteCatalog";

const PANEL_KEY_TO_ROUTE = {
  "manager-center": "/employee-panels/manager",
  orders: "/orders",
  appointments: "/appointments",
  quotes: "/quotes",
  reservations: "/reservations",
  "support-tickets": "/support-tickets",
  collections: "/collections",
  accounting: "/accounting",
  "social-media-manager": "/social-media-manager",
  "data-analyst": "/data-analyst",
  legal: "/legal",
  "tender-manager": "/tender-manager",
  "project-manager": "/project-manager",
  "sales-leads": "/sales-leads",
  enrollments: "/enrollments",
  memberships: "/memberships",
  "leave-requests": "/leave-requests",
  "property-inquiries": "/property-inquiries",
  construction: "/construction",
  salon: "/salon",
  vet: "/vet",
  dental: "/dental",
  aesthetic: "/estetik",
  gym: "/spor",
  education: "/kurs",
  hotel: "/otel",
  cafe: "/salon-yonetimi",
  law: "/buro",
  "tax-office": "/mukellef",
  "real-estate": "/satis-sureci",
  "ecommerce-ops": "/eticaret",
  "auto-service": "/servis-yonetimi",
  "pharmacy": "/eczane",
  "optic": "/optik",
  "daycare": "/kres",
  "venue": "/salon-takvimi",
  "account": "/cari",
  "production": "/uretim",
  "insurance": "/police",
  "care": "/program",
  "tour": "/turlar",
  "service-jobs": "/servis-kayitlari",
  "gallery": "/galeri",
  "recurring": "/tahsilat-plani",
  "onboarding-progress": "/onboarding-progress",
  "web-developer": "/web-developer",
  spider: "/spider",
  /* KİŞİSEL ASİSTAN — burada KAYITLI DEĞİLDİ ve panel hiç açılmıyordu:
     tek parçalı "/personal-assistant" rezerve sayılmadığı için uygulama onu
     "işletmenin halka açık sayfası" sanıp yönlendirmeyi atlıyordu (aynı tuzak
     daha önce /construction ve /salon'da yaşandı). Sektöre bağlı değildir —
     her işletmenin sahibinin hatırlatma/yapılacak/not defteridir. */
  "personal-assistant": "/personal-assistant",
};

/**
 * Panel rotalarının düz listesi. containers/App rezerve slug setinde kullanılıyor:
 * burada olmayan tek parçalı bir rota "halka açık site slug'ı" sanılıp panel
 * açılmıyordu (canlı bulgu: /construction ve /salon "Sayfa Bulunamadi" verdi).
 */
export const PANEL_ROUTE_VALUES = Object.keys(PANEL_KEY_TO_ROUTE).map((k) => PANEL_KEY_TO_ROUTE[k]);

export const normalizeRoutePath = (route) => {
  const raw = String(route || "").trim();
  if (!raw) return "/";
  const normalized = `/${raw.replace(/^\/+/, "").replace(/\/+$/, "")}`;
  return normalized === "//" ? "/" : normalized;
};

export const parseAllowedRoutes = (value) => {
  if (!value || value === "__all__") return [];
  if (Array.isArray(value)) return value.map(normalizeRoutePath).filter(Boolean);
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(normalizeRoutePath).filter(Boolean) : [];
  } catch (_error) {
    return [];
  }
};

export const getEffectiveUserRole = (authUser) => {
  const tenantRole = String((authUser && authUser.tenant_role) || "").trim().toLowerCase();
  if (tenantRole === "owner" || tenantRole === "admin" || tenantRole === "agent" || tenantRole === "viewer") {
    return tenantRole;
  }

  const role = String((authUser && authUser.role) || "").trim().toLowerCase();
  if (role === "owner" || role === "admin" || role === "sales" || role === "agent" || role === "viewer" || role === "manager") {
    return role;
  }

  return "agent";
};

const expandRouteCandidates = (route) => {
  const normalized = normalizeRoutePath(route);
  const candidates = new Set([normalized]);
  Object.keys(AGENT_ROUTE_ALIASES).forEach((parentRoute) => {
    const aliases = AGENT_ROUTE_ALIASES[parentRoute] || [];
    if (normalized === parentRoute || aliases.includes(normalized)) {
      candidates.add(parentRoute);
      aliases.forEach((alias) => candidates.add(alias));
    }
  });
  return Array.from(candidates);
};

const routesOverlap = (currentRoute, allowedRoute) => {
  const currentCandidates = expandRouteCandidates(currentRoute);
  const allowedCandidates = expandRouteCandidates(allowedRoute);
  return currentCandidates.some((currentCandidate) =>
    allowedCandidates.some(
      (allowedCandidate) =>
        currentCandidate === allowedCandidate
        || currentCandidate.indexOf(`${allowedCandidate}/`) === 0
        || allowedCandidate.indexOf(`${currentCandidate}/`) === 0
    )
  );
};

export const getPanelRouteFromKey = (panelKey) => {
  const normalizedKey = String(panelKey || "").trim();
  if (!normalizedKey) return "";
  if (normalizedKey.indexOf("employee-panel:") === 0) {
    const employeeKey = normalizedKey.split(":")[1] || "";
    return employeeKey ? `/employee-panels/${employeeKey.toLowerCase()}` : "";
  }
  return PANEL_KEY_TO_ROUTE[normalizedKey] || "";
};

export const buildRouteAccess = (authUser) => {
  const role = getEffectiveUserRole(authUser);
  const isOwnerAdmin = role === "owner" || role === "admin";
  const isSales = role === "sales";
  const isAgent = role === "agent";
  const isViewer = role === "viewer";
  const isManager = role === "manager";
  const allowedRoutes = parseAllowedRoutes(authUser && authUser.allowed_routes);
  const hasFullAccess = isOwnerAdmin
    || (isAgent && (!(authUser && authUser.allowed_routes) || authUser.allowed_routes === "__all__"))
    || ((isViewer || isManager) && authUser && authUser.allowed_routes === "__all__");

  const canAccessRoute = (route, options = {}) => {
    const normalizedRoute = normalizeRoutePath(route);
    if (isOwnerAdmin) return true;
    if (normalizedRoute === "/app-help") return true;
    if (options.ownerOnly) return false;
    if (isSales) {
      return normalizedRoute === "/chat" || normalizedRoute.indexOf("/chat/") === 0;
    }
    if (!isAgent && !isViewer && !isManager) return false;
    if (hasFullAccess) return true;
    return allowedRoutes.some((allowedRoute) => routesOverlap(normalizedRoute, allowedRoute));
  };

  return {
    role,
    isOwnerAdmin,
    isSales,
    isAgent,
    isViewer,
    isManager,
    allowedRoutes,
    hasFullAccess,
    canAccessRoute,
  };
};
